package com.qrkontaq.ui.admin

import androidx.compose.runtime.mutableStateOf
import com.qrkontaq.utils.Storage

/** Admin panel UI language — parity with the landing set (plus Polish). */
enum class AdminLanguage(val code: String, val shortLabel: String, val fullLabel: String) {
    UK("uk", "УКР", "Українська"),
    EN("en", "ENG", "English"),
    ES("es", "ESP", "Español"),
    PT("pt", "POR", "Português"),
    FR("fr", "FRA", "Français"),
    DE("de", "DEU", "Deutsch"),
    PL("pl", "POL", "Polski"),
    RU("ru", "РУС", "Русский"),
    ;

    val strings: AdminStrings
        get() = when (this) {
            UK -> AdminStringsUk
            EN -> AdminStringsEn
            ES -> AdminStringsEs
            PT -> AdminStringsPt
            FR -> AdminStringsFr
            DE -> AdminStringsDe
            PL -> AdminStringsPl
            RU -> AdminStringsRu
        }

    companion object {
        fun fromCode(code: String?): AdminLanguage = entries.firstOrNull { it.code == code } ?: EN
    }
}

private const val ADMIN_LANGUAGE_KEY = "admin_language"

/**
 * Saved admin choice wins; then the language the visitor picked on the LANDING
 * (a Spanish visitor clicking "Crear sitio" must not land on an English signup
 * form); then the system language; Spanish as the last resort — the landing
 * defaults to Spanish too, so an unsupported locale keeps reading the same
 * language it just saw there.
 */
private fun detectAdminLanguage(): AdminLanguage {
    val code =
        com.qrkontaq.utils.resolveLanguage(
            saved = Storage.get(ADMIN_LANGUAGE_KEY) ?: Storage.get("landing_language"),
            supported = AdminLanguage.entries.map { it.code },
            fallback = AdminLanguage.ES.code,
        )
    return AdminLanguage.entries.first { it.code == code }
}

private val adminLanguageState = mutableStateOf(detectAdminLanguage())

val adminLanguage: AdminLanguage get() = adminLanguageState.value

/** Current admin strings; reading it inside a composable subscribes to language changes. */
val AdminT: AdminStrings get() = adminLanguageState.value.strings

fun setAdminLanguage(language: AdminLanguage) {
    adminLanguageState.value = language
    Storage.save(ADMIN_LANGUAGE_KEY, language.code)
}

interface AdminStrings {
    // Tabs
    val tabDashboard: String
    val tabDashboardSub: String
    val tabMenu: String
    val tabMenuSub: String
    val tabStopList: String
    val tabStopListSub: String
    val tabTables: String
    val tabTablesSub: String
    val tabStaff: String
    val tabStaffSub: String
    val tabSettings: String
    val tabSettingsSub: String
    val tabSubscription: String
    val tabSubscriptionSub: String

    // Shell
    val openSite: String
    val logout: String
    val viewSite: String
    val roleOwner: String
    val roleManager: String
    val roleWaiter: String
    val roleCook: String
    val roleBartender: String
    val roleStaff: String

    // Dashboard
    val interfaceLanguage: String
    val pastDueBanner: String
    val pay: String
    val statScans: String
    val periodToday: String
    val periodWeek: String
    val periodMonth: String
    val periodYear: String
    val prevPeriod: String
    val nextPeriod: String
    val statVisits: String
    val statRating: String
    val statDishLikes: String
    val statRevenue: String

    // Personal overview of a cook / waiter
    val statCookedOrders: String
    val statCookedDishes: String
    val statDrinksMade: String
    val statServedOrders: String
    val statServedDishes: String
    val statTablesServed: String
    val myDishesCooked: String
    val myDrinksMade: String
    val myDishesServed: String
    val myTablesTitle: String
    val myTimelineTitle: String
    val myWorkEmpty: String
    val scansByDay: String
    fun maxBadge(value: String): String
    val peakHours: String
    val scansByTable: String
    val withoutTable: String
    fun tableN(label: String): String
    val topDishes: String
    val soldProducts: String

    // Dashboard empty states
    val dashEmptyTables: String
    val dashEmptyTablesHint: String
    val dashEmptySales: String
    val dashEmptySalesHint: String
    val dashEmptyTop: String
    val dashEmptyTopHint: String
    val dashEmptyCallsHint: String
    val dashEmptyPeakHint: String

    // Orders
    val tabOrders: String
    val tabOrdersSub: String
    val statusNew: String
    val statusCooking: String
    val statusReady: String
    val statusServed: String
    val takeOrder: String
    val markReady: String
    val markServed: String
    val newOrderTitle: String
    val addOrderBtn: String
    val addDishesHint: String
    val totalLabel: String
    val markPaid: String
    val confirmPaid: String
    val tableFree: String
    val noActiveOrders: String
    val ordersColumnEmpty: String
    fun orderTakenBy(name: String): String
    val orderOtherWaiter: String
    val closeMixedTableHint: String
    val waitingWaiter: String
    val closeLabel: String
    fun minutesShort(min: Long): String

    // Compact "time on the board": minutes, then hours, then days — never a huge minute count.
    fun agoShort(min: Long): String
    val orderStepBack: String

    // Auth
    val authCabinet: String
    val authRegisterSub: String
    val authShopName: String
    val authShopNamePh: String
    val authDomain: String
    val authDomainPh: String
    val authPassword: String
    val authPasswordHint: String
    val authSignIn: String
    val authCreateAccount: String
    val authCreateShop: String
    val authWizardSub: String
    val authNoShop: String
    val authHaveAccount: String
    val authNoAccount: String
    val authOr: String
    val authBack: String
    val authContinueGoogle: String
    val authContinueApple: String
    val authLoginFailed: String
    val authRegisterFailed: String

    // Field-specific auth errors (mapped from backend responses + client checks)
    val authErrEmailInvalid: String
    val authErrEmailTaken: String
    val authErrDomainTaken: String
    val authErrDomainRules: String
    val authErrPasswordRules: String
    val authErrInvalidCredentials: String
    val authErrOauthAccount: String
    val authErrOauthEmailTaken: String
    val authErrDeactivated: String
    val authErrDomainReserved: String
    val authErrNameRules: String

    val brandTagline: String
    val brandFeature1: String
    val brandFeature2: String
    val brandFeature3: String
    val brandFeature4: String
    val brandFeature6: String

    // Invite
    val inviteWelcome: String
    val inviteAccepting: String
    fun inviteJoined(role: String): String
    val inviteGoCabinet: String
    val inviteInvalid: String
    val inviteLoginHint: String

    // Waiter calls
    val hallCalls: String
    val legendWaiter: String
    val legendBill: String
    val asksBill: String
    val callsWaiter: String
    val done: String
    val markedDone: String
    val callUpdateFailed: String
    val layoutSaveFailed: String
    val bootFailed: String
    val bootRetry: String

    // Stop list
    fun inStop(count: Int): String
    fun totalDishes(count: Int): String
    val searchDishPh: String
    val nothingFound: String
    val tryAnotherQuery: String
    val outOfStock: String
    fun backInMenu(name: String): String
    val putToStop: String
    val updateFailed: String

    // Tables
    val addTableTitle: String
    val tableLabelPh: String
    val add: String
    val addTableFailed: String
    fun tablesCount(count: Int): String
    val qrPdf: String
    val preparingPdf: String
    fun deleteTable(label: String): String
    fun deleteTableConfirm(label: String): String
    val planHint: String
    val noTablesTitle: String
    val noTablesHint: String

    // Staff
    val inviteTitle: String
    val inviteHint: String
    val createInvite: String
    val inviteCreated: String
    val inviteCreateFailed: String
    val copy: String
    val copied: String
    fun teamCount(count: Int): String
    val removeFromTeam: String
    val removeFromTeamConfirm: String
    val activeInvites: String
    fun validUntil(date: String): String
    val cancel: String
    val teamMembersWord: String
    fun teamMemberSince(date: String): String
    val permWaiter: String
    val permCook: String
    val permBartender: String
    val permManager: String
    // Bar (drinks) category flag shown in the menu editor.
    val barCategory: String
    val barBadge: String

    // Subscription
    val currentState: String
    fun activeUntil(date: String): String
    val pastDueLong: String
    val badgeActive: String
    val badgePastDue: String
    val plan1Month: String
    val plan3Months: String
    val plan12Months: String
    val bestValueBadge: String
    val payMono: String
    val payHint: String
    val autoRenewLabel: String
    val autoRenewHint: String
    val autoRenewActive: (String?) -> String
    val autoRenewDisable: String
    val planActiveTitle: String
    val nextChargeOn: (String, String) -> String
    val autoRenewOffNote: (String) -> String
    val cancelSubscription: String
    val resumeAutoRenew: String
    val subActiveOverline: String
    val subEyebrow: String
    val subChooseTitle: String
    val subChooseSub: String
    val subFeaturesCap: String
    val subFeatures: List<String>
    val subCtaSubscribe: String
    val trustCancel: String
    val trustSecure: String
    val paymentHistory: String

    // Free/Pro plans
    val planFree: String
    val planPro: String
    val planFreeSub: String
    val planCurrent: String
    val planRecommended: String
    val perMonth: String
    val proLockTitle: String
    val proLockTeamBody: String
    val proUpgradeCta: String
    val proSectionHint: String
    val proLangHint: String

    // Menu tab
    val newCategory: String
    val addCategory: String
    val hiddenBadge: String
    val showLabel: String
    val hideLabel: String
    val moreActions: String
    val moveUp: String
    val moveDown: String
    val deleteCategory: String
    fun deleteCategoryConfirm(name: String): String
    val addDishPlus: String
    val copySuffix: String
    val duplicate: String
    val dishDuplicated: String
    val duplicateFailed: String
    val edit: String
    val delete: String
    fun deleteDishConfirm(name: String): String
    val dishDeleted: String
    val deleteFailed: String
    // Danger zone — permanently delete a venue (owner only)
    val dangerZoneTitle: String
    val dangerZoneHint: String
    val deleteVenueButton: String
    val deleteVenueModalTitle: String
    val deleteVenueModalBody: String
    fun deleteVenueConfirmLabel(name: String): String
    val deleteVenueConfirmPlaceholder: String
    val deleteVenueConfirmCta: String
    val deleteVenueSuccess: String
    val stopBadge: String
    val descriptionLabel: String
    val descriptionPh: String
    val priceLabel: String
    val weightLabel: String
    val weightPh: String
    val kcalLabel: String
    val tagsLabel: String
    val allergensLabel: String
    val tagVegan: String
    val tagSpicy: String
    val tagNew: String
    val allergenGluten: String
    val allergenLactose: String
    val allergenNuts: String
    val allergenEggs: String
    val allergenFish: String
    val allergenSoy: String
    val chefRecommends: String
    val uploadingEllipsis: String
    val addPhoto: String
    val replacePhoto: String
    val photoUploadFailed: String
    val addDish: String
    val save: String
    val dishAdded: String
    val savedToast: String
    val saveFailed: String
    val menuStatCategories: String
    val menuStatDishes: String
    val menuStatFeatured: String
    val menuEmptyTitle: String
    val menuEmptyHint: String
    val categoryLabel: String
    val editCategory: String
    val editDish: String
    val noCategory: String

    // Settings
    val sitePreviewTitle: String
    val sitePreviewHint: String
    val restaurantNamePh: String
    val brandTitle: String
    val logoLabel: String
    val coverLabel: String
    val nameLabel: String
    val shortDescription: String
    val accentHint: String
    val customColorLabel: String
    val contactsTitle: String
    val addressLabel: String
    val mapPickHint: String
    val mapFindByAddress: String
    val mapUseLocation: String
    val phoneLabel: String
    val socialsHint: String
    val workingHoursTitle: String
    val workingHoursHint: String
    val dayMon: String
    val dayTue: String
    val dayWed: String
    val dayThu: String
    val dayFri: String
    val daySat: String
    val daySun: String
    val dayOff: String
    val siteThemeTitle: String
    val siteThemeHint: String
    val themeAuto: String
    val themeLight: String
    val themeDark: String
    val menuLookTitle: String
    val categoryPhotosTitle: String
    val categoryPhotosHint: String
    val featureFirstTitle: String
    val featureFirstHint: String
    val showPopularTitle: String
    val showPopularHint: String
    val showChefTitle: String
    val showChefHint: String
    val showFiltersTitle: String
    val showFiltersHint: String
    val menuLanguagesTitle: String
    val menuLanguagesHint: String
    val saveChanges: String
    val savedCheck: String
    val savedSiteUpdated: String
    val saveFailedCheckColor: String
    val upload: String
    val replace: String

    // Settings: currency + loyalty program
    val currencyTitle: String
    val currencyHint: String
    val guestServicesTitle: String
    val guestServicesHint: String
    val guestCheckTitle: String
    val guestCheckHint: String
    val waiterCallTitle: String
    val waiterCallHint: String
    val loyaltyTitle: String
    val loyaltyEnableTitle: String
    val loyaltyEnableHint: String
    val loyaltyRateLabel: String
    val loyaltyRateHint: String

    // Loyalty at close-table time
    val loyaltyGuestTitle: String
    val loyaltyCandidatesTitle: String
    val loyaltyCodePh: String
    val loyaltyScanQr: String
    val loyaltyFindGuest: String
    val loyaltyCodeNotFound: String
    val loyaltyCodeExpired: String
    val loyaltyCheckFailed: String
    val loyaltyBalanceLabel: String
    val loyaltyRedeemLabel: String
    val loyaltyEarnedLabel: String
    val loyaltyRedeemedLabel: String
    val loyaltyNewBalanceLabel: String
    val loyaltyClear: String
    val loyaltyCameraFailed: String
    val loyaltyGuestAtTable: String
    val loyaltyNewGuest: String
    val loyaltyVisitsSuffix: String
    val loyaltyCallingWaiter: String
    val loyaltyCallingBill: String
}

object AdminStringsUk : AdminStrings {
    override val tabDashboard = "Огляд"
    override val tabDashboardSub = "Що відбувається із закладом сьогодні"
    override val tabMenu = "Меню"
    override val tabMenuSub = "Категорії, страви, ціни та стоп-лист"
    override val tabStopList = "Стоп-лист"
    override val tabStopListSub = "Страва закінчилась? Один дотик"
    override val tabTables = "Столи"
    override val tabTablesSub = "Виклики залу та QR-коди для друку"
    override val tabStaff = "Команда"
    override val tabStaffSub = "Запрошення та ролі співробітників"
    override val tabSettings = "Налаштування"
    override val tabSettingsSub = "Бренд, контакти, години роботи"
    override val tabSubscription = "Підписка"
    override val tabSubscriptionSub = ""

    override val openSite = "Відкрити сайт"
    override val logout = "Вийти"
    override val viewSite = "переглянути сайт"
    override val roleOwner = "Власник"
    override val roleManager = "Менеджер"
    override val roleWaiter = "Офіціант"
    override val roleCook = "Кухар"
    override val roleBartender = "Бармен"
    override val roleStaff = "Співробітник"

    override val interfaceLanguage = "Мова панелі"
    override val pastDueBanner = "Оплачений період закінчився. Pro-функції працюють ще кілька днів."
    override val pay = "Оплатити"
    override val statScans = "Сканування QR"
    override val periodToday = "Сьогодні"
    override val periodWeek = "Тиждень"
    override val periodMonth = "Місяць"
    override val periodYear = "Рік"
    override val prevPeriod = "Попередній період"
    override val nextPeriod = "Наступний період"
    override val statVisits = "Візитів"
    override val statRevenue = "Виручка"
    override val statRating = "Рейтинг"
    override val statDishLikes = "Лайків страв"
    override val statCookedOrders = "Замовлень приготовано"
    override val statCookedDishes = "Страв приготовано"
    override val statDrinksMade = "Напоїв зроблено"
    override val statServedOrders = "Замовлень прийнято"
    override val statServedDishes = "Товарів прийнято"
    override val statTablesServed = "Столів обслужено"
    override val myDishesCooked = "Що приготовано"
    override val myDrinksMade = "Які напої зроблено"
    override val myDishesServed = "Що прийнято"
    override val myTablesTitle = "Столи"
    override val myTimelineTitle = "Хронологія"
    override val myWorkEmpty = "За цей період ще немає даних"
    override val scansByDay = "Динаміка сканувань"
    override fun maxBadge(value: String) = "макс. $value"
    override val peakHours = "Пік годин (скани)"
    override val scansByTable = "Скани по столах"
    override val withoutTable = "без столу"
    override fun tableN(label: String) = "Стіл $label"
    override val topDishes = "Улюблені страви гостей"
    override val soldProducts = "Продані товари"
    override val dashEmptyTables = "Сканувань поки немає"
    override val dashEmptyTablesHint = "Роздрукуйте QR-коди столів — тут з'явиться статистика по кожному столу"
    override val dashEmptySales = "Продажів поки немає"
    override val dashEmptySalesHint = "Щойно гості почнуть замовляти, тут з'явиться виручка по стравах"
    override val dashEmptyTop = "Лайків поки немає"
    override val dashEmptyTopHint = "Гості можуть вподобати страви в меню — найкращі з'являться тут"
    override val dashEmptyCallsHint = "Викликів за цей період не було"
    override val dashEmptyPeakHint = "Дані з'являться після перших сканувань QR"

    override val tabOrders = "Замовлення"
    override val tabOrdersSub = "Кухня і зал: статуси страв у реальному часі"
    override val statusNew = "Нове"
    override val statusCooking = "Готується"
    override val statusReady = "Готове"
    override val statusServed = "Подано"
    override val takeOrder = "Взяти в роботу"
    override val markReady = "Готово"
    override val markServed = "Подано"
    override val newOrderTitle = "Нове замовлення"
    override val addOrderBtn = "Додати замовлення"
    override val addDishesHint = "Додайте страви з меню"
    override val totalLabel = "Разом"
    override val markPaid = "Оплачено"
    override val confirmPaid = "Позначити оплаченим і звільнити стіл?"
    override val tableFree = "Вільний"
    override val noActiveOrders = "Немає активних замовлень"
    override val ordersColumnEmpty = "Порожньо"
    override fun orderTakenBy(name: String) = "Прийняв: $name"
    override val orderOtherWaiter = "Замовлення іншого офіціанта"
    override val closeMixedTableHint = "На столі є замовлення іншого офіціанта — закрити може менеджер"
    override val waitingWaiter = "Чекає офіціанта"
    override val closeLabel = "Закрити"
    override fun minutesShort(min: Long) = "$min хв"
    override fun agoShort(min: Long) = when {
        min < 60 -> "$min хв"
        min < 1440 -> "${min / 60} год"
        else -> "${min / 1440} дн"
    }
    override val orderStepBack = "Повернути назад"
    override val authCabinet = "Кабінет вашого закладу"
    override val authRegisterSub = "Створіть акаунт для входу"
    override val authShopName = "Назва ресторану"
    override val authShopNamePh = "Кав’ярня «Затишок»"
    override val authDomain = "Домен"
    override val authDomainPh = "zatyshok"
    override val authPassword = "Пароль"
    override val authSignIn = "Увійти"
    override val authCreateAccount = "Створити акаунт"
    override val authCreateShop = "Створити заклад"
    override val authWizardSub = "Останній крок — створіть свій заклад"
    override val authNoShop = "Ще немає закладу? Зареєструвати"
    override val authHaveAccount = "Вже зареєстровані? Увійти"
    override val authNoAccount = "Немає акаунта? Зареєструватися"
    override val authOr = "або"
    override val authBack = "Назад"
    override val authContinueGoogle = "Продовжити з Google"
    override val authContinueApple = "Продовжити з Apple"
    override val authLoginFailed = "Невірний email або пароль."
    override val authRegisterFailed = "Не вдалося зареєструватися. Перевірте дані."
    override val authErrEmailInvalid = "Вкажіть коректний email — наприклад, you@example.com."
    override val authErrEmailTaken = "Цей email вже зареєстрований. Спробуйте увійти."
    override val authErrDomainTaken = "Ця адреса сайту вже зайнята — оберіть іншу."
    override val authErrDomainRules =
        "Адреса сайту: 3–30 символів, лише латинські літери, цифри та дефіс; починається й закінчується літерою або цифрою."
    override val authPasswordHint = "Мінімум 6 символів"
    override val authErrPasswordRules =
        "Пароль закороткий: потрібно щонайменше 6 символів."
    override val authErrInvalidCredentials = "Невірний email або пароль."
    override val authErrOauthAccount = "Цей акаунт входить через Google/Apple — скористайтеся цією кнопкою входу."
    override val authErrOauthEmailTaken = "Цей email вже зареєстровано з паролем — увійдіть з email і паролем."
    override val authErrDeactivated = "Акаунт деактивовано. Напишіть у підтримку."
    override val authErrDomainReserved = "Ця адреса зарезервована системою — оберіть іншу."
    override val authErrNameRules = "Назва закладу: від 1 до 100 символів."
    override val brandTagline = "Свій сайт за 1 хвилину — і одразу до роботи"
    override val brandFeature1 = "QR-меню кількома мовами"
    override val brandFeature2 = "Зал і кухня в реальному часі"
    override val brandFeature3 = "Статистика, стоп-лист і команда"
    override val brandFeature4 = "Замовлення, рахунки й оплата"
    override val brandFeature6 = "Безкоштовний план, без картки"
    override val inviteWelcome = "Приєднання до команди"
    override val inviteAccepting = "Приймаємо запрошення…"
    override fun inviteJoined(role: String) = "Ви приєдналися як $role"
    override val inviteGoCabinet = "Перейти в кабінет"
    override val inviteInvalid = "Запрошення недійсне або протерміноване"
    override val inviteLoginHint = "Увійдіть або зареєструйтеся, щоб прийняти запрошення"
    override val hallCalls = "Виклики з залу"
    override val legendWaiter = "Офіціант"
    override val legendBill = "Рахунок"
    override val asksBill = "просить рахунок"
    override val callsWaiter = "кличе офіціанта"
    override val done = "Виконано"
    override val markedDone = "Позначено виконаним"
    override val callUpdateFailed = "Не вдалося оновити виклик"
    override val layoutSaveFailed = "Не вдалося зберегти розташування столів"
    override val bootFailed = "Не вдалося з'єднатися з сервером. Перевірте інтернет і спробуйте ще раз."
    override val bootRetry = "Спробувати ще раз"

    override fun inStop(count: Int) = "У стопі: $count"
    override fun totalDishes(count: Int) = "Всього страв: $count"
    override val searchDishPh = "Пошук страви…"
    override val nothingFound = "Нічого не знайдено"
    override val tryAnotherQuery = "Спробуйте інший запит"
    override val outOfStock = "немає в наявності"
    override fun backInMenu(name: String) = "«$name» знову в меню"
    override val putToStop = "Поставлено в стоп"
    override val updateFailed = "Не вдалося оновити"

    override val addTableTitle = "Додати стіл"
    override val tableLabelPh = "Напр. 5 або Тераса 2"
    override val add = "Додати"
    override val addTableFailed = "Не вдалося додати стіл"
    override fun tablesCount(count: Int) = "Столи ($count)"
    override val qrPdf = "QR у PDF"
    override val preparingPdf = "Готуємо PDF…"
    override fun deleteTable(label: String) = "Видалити стіл $label"
    override fun deleteTableConfirm(label: String) = "Видалити стіл $label?"
    override val planHint = "Перетягуйте столи, щоб розставити їх як у залі. Розташування зберігається в цьому браузері."
    override val noTablesTitle = "Ще немає столів"
    override val noTablesHint = "Додайте столи — і згенеруємо PDF із QR-кодами для друку"

    override val inviteTitle = "Запросити співробітника"
    override val inviteHint = "Офіціант — столи та замовлення. Кухар — замовлення (столи лише перегляд). Менеджер — усе, крім підписки."
    override val createInvite = "Створити запрошення"
    override val inviteCreated = "Запрошення створено"
    override val inviteCreateFailed = "Не вдалося створити запрошення"
    override val copy = "Копіювати"
    override val copied = "Скопійовано"
    override fun teamCount(count: Int) = "Команда ($count)"
    override val removeFromTeam = "Прибрати з команди"
    override val removeFromTeamConfirm = "Прибрати з команди?"
    override val activeInvites = "Активні запрошення:"
    override fun validUntil(date: String) = "діє до $date"
    override val cancel = "Скасувати"
    override val teamMembersWord = "учасників"
    override fun teamMemberSince(date: String) = "у команді з $date"
    override val permWaiter = "Столи та замовлення"
    override val permCook = "Замовлення на кухню"
    override val permBartender = "Замовлення на бар"
    override val permManager = "Усе, крім підписки"
    override val barCategory = "Барна категорія (напої)"
    override val barBadge = "Бар"

    override val currentState = "Поточний стан"
    override fun activeUntil(date: String) = "Активна до $date"
    override val pastDueLong = "Оплата прострочена — Pro-функції скоро вимкнуться"
    override val badgeActive = "Активна"
    override val badgePastDue = "Прострочено"
    override val plan1Month = "1 місяць"
    override val plan3Months = "3 місяці"
    override val plan12Months = "12 місяців"
    override val bestValueBadge = "-17% · вигідно"
    override val payMono = "Вибрати"
    override val payHint = "Оплата через захищену сторінку monobank. Після оплати підписка продовжиться автоматично."
    override val autoRenewLabel = "Автопродовження"
    override val autoRenewHint = "Картка збережеться після оплати, і підписка продовжуватиметься автоматично. Вимкнути можна будь-коли."
    override val autoRenewActive = { mask: String? ->
        "Автопродовження увімкнено" + (mask?.let { " · картка •••• $it" } ?: "")
    }
    override val autoRenewDisable = "Вимкнути"
    override val planActiveTitle = "План активовано"
    override val nextChargeOn = { date: String, amount: String -> "Наступне списання $date · $amount" }
    override val autoRenewOffNote = { date: String -> "Автопродовження вимкнено — доступ діє до $date" }
    override val cancelSubscription = "Скасувати підписку"
    override val resumeAutoRenew = "Відновити автопродовження"
    override val subActiveOverline = "Підписка активна"
    override val subEyebrow = "Тарифи"
    override val subChooseTitle = "Free або Pro"
    override val subChooseSub = "Меню, замовлення та столи — безкоштовно назавжди. Pro відкриває сервіси для гостей і команду."
    override val subFeaturesCap = "Входить у Pro"
    override val subFeatures = listOf(
        "Програма лояльності з кешбеком",
        "Кнопка виклику офіціанта",
        "Рахунок столу для гостей",
        "Меню кількома мовами",
        "Розділи «Популярне» і «Шеф рекомендує»",
        "Команда та запрошення персоналу",
    )
    override val subCtaSubscribe = "Перейти на Pro"
    override val trustCancel = "Скасувати будь-коли"
    override val trustSecure = "Захищена оплата"
    override val paymentHistory = "Історія платежів"

    override val planFree = "Free"
    override val planPro = "Pro"
    override val planFreeSub = "меню без обмежень"
    override val planCurrent = "Ваш план"
    override val planRecommended = "Рекомендуємо"
    override val perMonth = "на місяць"
    override val proLockTitle = "Доступно на Pro"
    override val proLockTeamBody = "Запрошуйте офіціантів, кухарів і менеджерів на тарифі Pro. Поточні учасники команди залишаються."
    override val proUpgradeCta = "Перейти на Pro"
    override val proSectionHint = "Доступно на тарифі Pro — увімкніть його у вкладці «Підписка»."
    override val proLangHint = "На Free активна одна мова меню — змінити її можна будь-коли. Усі мови відкриває Pro."

    override val newCategory = "Нова категорія"
    override val addCategory = "Додати категорію"
    override val hiddenBadge = "приховано"
    override val showLabel = "Показати"
    override val hideLabel = "Приховати"
    override val moreActions = "Більше дій"
    override val moveUp = "Вгору"
    override val moveDown = "Вниз"
    override val deleteCategory = "Видалити категорію"
    override fun deleteCategoryConfirm(name: String) = "Видалити категорію «$name» разом зі стравами?"
    override val addDishPlus = "＋ Додати страву"
    override val copySuffix = " (копія)"
    override val duplicate = "Дублювати"
    override val dishDuplicated = "Страву продубльовано"
    override val duplicateFailed = "Не вдалося дублювати"
    override val edit = "Редагувати"
    override val delete = "Видалити"
    override fun deleteDishConfirm(name: String) = "Видалити «$name»?"
    override val dishDeleted = "Страву видалено"
    override val deleteFailed = "Не вдалося видалити"
    override val dangerZoneTitle = "Небезпечна зона"
    override val dangerZoneHint =
        "Видалення заведення незворотне: зникнуть усе меню, фото, столи, замовлення, команда, лояльність і адреса сайту."
    override val deleteVenueButton = "Видалити заведення"
    override val deleteVenueModalTitle = "Видалити заведення?"
    override val deleteVenueModalBody =
        "Цю дію не можна скасувати. Назавжди буде видалено меню й фото, столи та QR-коди, " +
            "замовлення й статистику, команду та запрошення, програму лояльності й адресу сайту."
    override fun deleteVenueConfirmLabel(name: String) = "Щоб підтвердити, введіть назву «$name»"
    override val deleteVenueConfirmPlaceholder = "Назва заведення"
    override val deleteVenueConfirmCta = "Видалити назавжди"
    override val deleteVenueSuccess = "Заведення видалено"
    override val stopBadge = "стоп"
    override val descriptionLabel = "Опис"
    override val descriptionPh = "Напр.: Томати, моцарела, свіжий базилік"
    override val priceLabel = "Ціна"
    override val weightLabel = "Вага/об'єм"
    override val weightPh = "320 г"
    override val kcalLabel = "Ккал"
    override val tagsLabel = "Теги"
    override val allergensLabel = "Алергени"
    override val tagVegan = "Веган"
    override val tagSpicy = "Гостре"
    override val tagNew = "Новинка"
    override val allergenGluten = "Глютен"
    override val allergenLactose = "Лактоза"
    override val allergenNuts = "Горіхи"
    override val allergenEggs = "Яйця"
    override val allergenFish = "Риба"
    override val allergenSoy = "Соя"
    override val chefRecommends = "Рекомендує шеф"
    override val uploadingEllipsis = "Завантаження…"
    override val addPhoto = "Додати фото"
    override val replacePhoto = "Замінити фото"
    override val photoUploadFailed = "Не вдалося завантажити фото"
    override val addDish = "Додати страву"
    override val save = "Зберегти"
    override val dishAdded = "Страву додано"
    override val savedToast = "Збережено"
    override val saveFailed = "Не вдалося зберегти"
    override val menuStatCategories = "категорій"
    override val menuStatDishes = "страв"
    override val menuStatFeatured = "хітів"
    override val menuEmptyTitle = "Меню поки порожнє"
    override val menuEmptyHint = "Створіть першу категорію — і почніть додавати страви."
    override val categoryLabel = "Категорія"
    override val editCategory = "Редагувати категорію"
    override val editDish = "Редагувати страву"
    override val noCategory = "Без категорії"

    override val sitePreviewTitle = "Як виглядатиме сайт"
    override val sitePreviewHint = "Превʼю оновлюється одразу — збережіть, щоб застосувати на сайті."
    override val restaurantNamePh = "Назва закладу"
    override val brandTitle = "Бренд"
    override val logoLabel = "Логотип"
    override val coverLabel = "Обкладинка"
    override val nameLabel = "Назва"
    override val shortDescription = "Короткий опис"
    override val accentHint = "Акцентний колір — кнопки, ціни й підсвітки на сайті закладу"
    override val customColorLabel = "Або свій колір"
    override val contactsTitle = "Контакти"
    override val addressLabel = "Адреса"
    override val mapPickHint = "Пересуньте карту так, щоб маркер вказував на заклад"
    override val mapFindByAddress = "Знайти за адресою"
    override val mapUseLocation = "Моє місцезнаходження"
    override val phoneLabel = "Телефон"
    override val socialsHint = "Соцмережі — показуються гостям у розділі «Про нас» на сайті"
    override val workingHoursTitle = "Години роботи"
    override val workingHoursHint = "Порожньо = вихідний. Формат 09:00 / 22:00, підтримується і через північ (18:00–02:00)."
    override val dayMon = "Пн"
    override val dayTue = "Вт"
    override val dayWed = "Ср"
    override val dayThu = "Чт"
    override val dayFri = "Пт"
    override val daySat = "Сб"
    override val daySun = "Нд"
    override val dayOff = "вихідний"
    override val siteThemeTitle = "Тема сайту"
    override val siteThemeHint = "Як виглядає меню у гостя: світла, темна або автоматично за налаштуваннями телефона."
    override val themeAuto = "Авто (як у гостя)"
    override val themeLight = "Світла"
    override val themeDark = "Темна"
    override val menuLookTitle = "Вигляд меню"
    override val categoryPhotosTitle = "Фото в категоріях"
    override val categoryPhotosHint = "Чип категорії показує фото першої страви; без фото — лише текст"
    override val featureFirstTitle = "Перша страва великою карткою"
    override val featureFirstHint = "Перша страва категорії — велике фото на всю ширину; вимкнено — як усі інші"
    override val showPopularTitle = "Блок «Популярне»"
    override val showPopularHint = "Показувати добірку популярних страв угорі меню"
    override val showChefTitle = "Блок «Рекомендує шеф»"
    override val showChefHint = "Показувати добірку страв, які рекомендує шеф"
    override val showFiltersTitle = "Кнопка фільтрів"
    override val showFiltersHint = "Показувати кнопку фільтрів навпроти категорій меню"
    override val menuLanguagesTitle = "Мови меню"
    override val menuLanguagesHint = "Гість зможе перемикати ці мови на сайті. Заповнюйте назви й описи обраними мовами в меню."
    override val saveChanges = "Зберегти зміни"
    override val savedCheck = "Збережено ✓"
    override val savedSiteUpdated = "Збережено — сайт оновлено"
    override val saveFailedCheckColor = "Не вдалося зберегти (перевірте колір #RRGGBB)"
    override val upload = "Завантажити"
    override val replace = "Замінити"

    override val currencyTitle = "Валюта"
    override val currencyHint = "Валюта цін у меню та бонусів гостей."
    override val guestServicesTitle = "Сервіси за столом"
    override val guestServicesHint = "Що гість бачить на сторінці столу після сканування QR"
    override val guestCheckTitle = "Показувати чек гостю"
    override val guestCheckHint = "Гість бачить свій рахунок у реальному часі: позиції, кількість і суму"
    override val waiterCallTitle = "Виклик офіціанта"
    override val waiterCallHint = "Гість може покликати офіціанта або попросити рахунок"
    override val loyaltyTitle = "Програма лояльності"
    override val loyaltyEnableTitle = "Кешбек для гостей"
    override val loyaltyEnableHint = "Гість накопичує бонуси з кожного чека та списує їх під час оплати"
    override val loyaltyRateLabel = "Відсоток кешбеку з чека (1–50)"
    override val loyaltyRateHint = "Стільки відсотків від суми чека повернеться гостю бонусами"
    override val loyaltyGuestTitle = "Бонуси гостя"
    override val loyaltyCandidatesTitle = "Нещодавно сканували цей стіл"
    override val loyaltyCodePh = "6-значний код"
    override val loyaltyScanQr = "Сканувати QR"
    override val loyaltyFindGuest = "Знайти гостя"
    override val loyaltyCodeNotFound = "Код не знайдено"
    override val loyaltyCodeExpired = "Код прострочено — попросіть гостя показати новий"
    override val loyaltyCheckFailed = "Не вдалося перевірити код"
    override val loyaltyBalanceLabel = "Баланс"
    override val loyaltyRedeemLabel = "Списати бонуси"
    override val loyaltyEarnedLabel = "Нараховано"
    override val loyaltyRedeemedLabel = "Списано"
    override val loyaltyNewBalanceLabel = "Новий баланс"
    override val loyaltyClear = "Очистити"
    override val loyaltyCameraFailed = "Камера недоступна — введіть код вручну"
    override val loyaltyGuestAtTable = "Гість за столом"
    override val loyaltyNewGuest = "Новий гість"
    override val loyaltyVisitsSuffix = "візитів"
    override val loyaltyCallingWaiter = "Кличе офіціанта"
    override val loyaltyCallingBill = "Просить рахунок"
}

object AdminStringsEn : AdminStrings {
    override val tabDashboard = "Overview"
    override val tabDashboardSub = "What's happening at your place today"
    override val tabMenu = "Menu"
    override val tabMenuSub = "Categories, dishes, prices and the stop list"
    override val tabStopList = "Stop list"
    override val tabStopListSub = "Dish ran out? One tap"
    override val tabTables = "Tables"
    override val tabTablesSub = "Hall calls and printable QR codes"
    override val tabStaff = "Team"
    override val tabStaffSub = "Invitations and staff roles"
    override val tabSettings = "Settings"
    override val tabSettingsSub = "Brand, contacts, opening hours"
    override val tabSubscription = "Subscription"
    override val tabSubscriptionSub = ""

    override val openSite = "Open site"
    override val logout = "Log out"
    override val viewSite = "view site"
    override val roleOwner = "Owner"
    override val roleManager = "Manager"
    override val roleWaiter = "Waiter"
    override val roleCook = "Cook"
    override val roleBartender = "Bartender"
    override val roleStaff = "Staff"

    override val interfaceLanguage = "Panel language"
    override val pastDueBanner = "The paid period has ended. Pro features keep working for a few more days."
    override val pay = "Pay"
    override val statScans = "QR scans"
    override val periodToday = "Today"
    override val periodWeek = "Week"
    override val periodMonth = "Month"
    override val periodYear = "Year"
    override val prevPeriod = "Previous period"
    override val nextPeriod = "Next period"
    override val statVisits = "Visits"
    override val statRevenue = "Revenue"
    override val statRating = "Rating"
    override val statDishLikes = "Dish likes"
    override val statCookedOrders = "Orders cooked"
    override val statCookedDishes = "Dishes cooked"
    override val statDrinksMade = "Drinks made"
    override val statServedOrders = "Orders accepted"
    override val statServedDishes = "Items accepted"
    override val statTablesServed = "Tables served"
    override val myDishesCooked = "What you cooked"
    override val myDrinksMade = "Drinks you made"
    override val myDishesServed = "What you accepted"
    override val myTablesTitle = "Tables"
    override val myTimelineTitle = "Timeline"
    override val myWorkEmpty = "Nothing here for this period yet"
    override val scansByDay = "Scan trend"
    override fun maxBadge(value: String) = "max $value"
    override val peakHours = "Peak hours (scans)"
    override val scansByTable = "Scans by table"
    override val withoutTable = "no table"
    override fun tableN(label: String) = "Table $label"
    override val topDishes = "Guests' favourite dishes"
    override val soldProducts = "Sold products"
    override val dashEmptyTables = "No scans yet"
    override val dashEmptyTablesHint = "Print the table QR codes — per-table stats will appear here"
    override val dashEmptySales = "No sales yet"
    override val dashEmptySalesHint = "Once guests start ordering, dish revenue will show up here"
    override val dashEmptyTop = "No likes yet"
    override val dashEmptyTopHint = "Guests can like dishes in the menu — favourites will appear here"
    override val dashEmptyCallsHint = "No calls during this period"
    override val dashEmptyPeakHint = "Data will appear after the first QR scans"

    override val tabOrders = "Orders"
    override val tabOrdersSub = "Kitchen & floor: dish statuses in real time"
    override val statusNew = "New"
    override val statusCooking = "Cooking"
    override val statusReady = "Ready"
    override val statusServed = "Served"
    override val takeOrder = "Start cooking"
    override val markReady = "Ready"
    override val markServed = "Served"
    override val newOrderTitle = "New order"
    override val addOrderBtn = "Add order"
    override val addDishesHint = "Add dishes from the menu"
    override val totalLabel = "Total"
    override val markPaid = "Paid"
    override val confirmPaid = "Mark as paid and free the table?"
    override val tableFree = "Free"
    override val noActiveOrders = "No active orders"
    override val ordersColumnEmpty = "Empty"
    override fun orderTakenBy(name: String) = "Taken by $name"
    override val orderOtherWaiter = "Another waiter's order"
    override val closeMixedTableHint = "This table has another waiter's order — a manager can close it"
    override val waitingWaiter ="Waiting for the waiter"
    override val closeLabel = "Close"
    override fun minutesShort(min: Long) = "$min min"
    override fun agoShort(min: Long) = when {
        min < 60 -> "$min m"
        min < 1440 -> "${min / 60} h"
        else -> "${min / 1440} d"
    }
    override val orderStepBack = "Move back"
    override val authCabinet = "Your restaurant's dashboard"
    override val authRegisterSub = "Create an account to sign in"
    override val authShopName = "Restaurant name"
    override val authShopNamePh = "Cafe «Coziness»"
    override val authDomain = "Domain"
    override val authDomainPh = "coziness"
    override val authPassword = "Password"
    override val authSignIn = "Sign in"
    override val authCreateAccount = "Create account"
    override val authCreateShop = "Create the place"
    override val authWizardSub = "One last step — create your venue"
    override val authNoShop = "No place yet? Register"
    override val authHaveAccount = "Already registered? Sign in"
    override val authNoAccount = "No account? Register"
    override val authOr = "or"
    override val authBack = "Back"
    override val authContinueGoogle = "Continue with Google"
    override val authContinueApple = "Continue with Apple"
    override val authLoginFailed = "Wrong email or password."
    override val authRegisterFailed = "Couldn't register. Check the details."
    override val authErrEmailInvalid = "Enter a valid email — e.g. you@example.com."
    override val authErrEmailTaken = "This email is already registered. Try signing in."
    override val authErrDomainTaken = "This site address is taken — pick another one."
    override val authErrDomainRules =
        "Site address: 3–30 characters, only Latin letters, digits and hyphens; must start and end with a letter or digit."
    override val authPasswordHint = "At least 6 characters"
    override val authErrPasswordRules =
        "Password is too short: at least 6 characters required."
    override val authErrInvalidCredentials = "Wrong email or password."
    override val authErrOauthAccount = "This account signs in with Google/Apple — use that button instead."
    override val authErrOauthEmailTaken = "This email is already registered with a password — sign in with your email and password."
    override val authErrDeactivated = "This account is deactivated. Contact support."
    override val authErrDomainReserved = "This address is reserved by the system — pick another one."
    override val authErrNameRules = "Venue name: 1 to 100 characters."
    override val brandTagline = "Your own site in 1 minute — and start using it right away"
    override val brandFeature1 = "QR menu in several languages"
    override val brandFeature2 = "Floor and kitchen in real time"
    override val brandFeature3 = "Stats, stop-list and team"
    override val brandFeature4 = "Orders, bills and payment"
    override val brandFeature6 = "Free plan, no card"
    override val inviteWelcome = "Joining the team"
    override val inviteAccepting = "Accepting the invitation…"
    override fun inviteJoined(role: String) = "You joined as $role"
    override val inviteGoCabinet = "Go to the panel"
    override val inviteInvalid = "The invitation is invalid or has expired"
    override val inviteLoginHint = "Sign in or register to accept the invitation"
    override val hallCalls = "Calls from the hall"
    override val legendWaiter = "Waiter"
    override val legendBill = "Bill"
    override val asksBill = "asks for the bill"
    override val callsWaiter = "is calling a waiter"
    override val done = "Done"
    override val markedDone = "Marked as done"
    override val callUpdateFailed = "Failed to update the call"
    override val layoutSaveFailed = "Failed to save the table layout"
    override val bootFailed = "Couldn't reach the server. Check your connection and try again."
    override val bootRetry = "Try again"

    override fun inStop(count: Int) = "Stopped: $count"
    override fun totalDishes(count: Int) = "Total dishes: $count"
    override val searchDishPh = "Search dishes…"
    override val nothingFound = "Nothing found"
    override val tryAnotherQuery = "Try another query"
    override val outOfStock = "out of stock"
    override fun backInMenu(name: String) = "“$name” is back on the menu"
    override val putToStop = "Put on the stop list"
    override val updateFailed = "Failed to update"

    override val addTableTitle = "Add a table"
    override val tableLabelPh = "E.g. 5 or Terrace 2"
    override val add = "Add"
    override val addTableFailed = "Failed to add the table"
    override fun tablesCount(count: Int) = "Tables ($count)"
    override val qrPdf = "QR to PDF"
    override val preparingPdf = "Preparing PDF…"
    override fun deleteTable(label: String) = "Delete table $label"
    override fun deleteTableConfirm(label: String) = "Delete table $label?"
    override val planHint = "Drag tables to arrange them like in your hall. The layout is saved in this browser."
    override val noTablesTitle = "No tables yet"
    override val noTablesHint = "Add tables — we'll generate a printable PDF with QR codes"

    override val inviteTitle = "Invite an employee"
    override val inviteHint = "Waiter — tables and orders. Cook — orders (tables view-only). Manager — everything except the subscription."
    override val createInvite = "Create invitation"
    override val inviteCreated = "Invitation created"
    override val inviteCreateFailed = "Failed to create the invitation"
    override val copy = "Copy"
    override val copied = "Copied"
    override fun teamCount(count: Int) = "Team ($count)"
    override val removeFromTeam = "Remove from the team"
    override val removeFromTeamConfirm = "Remove from the team?"
    override val activeInvites = "Active invitations:"
    override fun validUntil(date: String) = "valid until $date"
    override val cancel = "Cancel"
    override val teamMembersWord = "members"
    override fun teamMemberSince(date: String) = "on the team since $date"
    override val permWaiter = "Tables and orders"
    override val permCook = "Kitchen orders"
    override val permBartender = "Bar orders"
    override val permManager = "Everything but billing"
    override val barCategory = "Bar category (drinks)"
    override val barBadge = "Bar"

    override val currentState = "Current status"
    override fun activeUntil(date: String) = "Active until $date"
    override val pastDueLong = "Payment overdue — Pro features will pause soon"
    override val badgeActive = "Active"
    override val badgePastDue = "Past due"
    override val plan1Month = "1 month"
    override val plan3Months = "3 months"
    override val plan12Months = "12 months"
    override val bestValueBadge = "-17% · best value"
    override val payMono = "Choose"
    override val autoRenewLabel = "Auto-renewal"
    override val autoRenewHint = "Your card is saved after payment and the subscription renews automatically. You can turn this off anytime."
    override val autoRenewActive = { mask: String? ->
        "Auto-renewal is on" + (mask?.let { " · card •••• $it" } ?: "")
    }
    override val autoRenewDisable = "Turn off"
    override val planActiveTitle = "Plan active"
    override val nextChargeOn = { date: String, amount: String -> "Next charge $date · $amount" }
    override val autoRenewOffNote = { date: String -> "Auto-renewal is off — access lasts until $date" }
    override val cancelSubscription = "Cancel subscription"
    override val resumeAutoRenew = "Resume auto-renewal"
    override val subActiveOverline = "Subscription active"
    override val subEyebrow = "Plans"
    override val subChooseTitle = "Free or Pro"
    override val subChooseSub = "Menu, orders and tables are free forever. Pro unlocks guest services and your team."
    override val subFeaturesCap = "Included in Pro"
    override val subFeatures = listOf(
        "Loyalty program with cashback",
        "Waiter call button",
        "Table bill for guests",
        "Menu in multiple languages",
        "“Popular” and “Chef recommends” sections",
        "Team and staff invitations",
    )
    override val subCtaSubscribe = "Upgrade to Pro"
    override val trustCancel = "Cancel anytime"
    override val trustSecure = "Secure payment"

    override val planFree = "Free"
    override val planPro = "Pro"
    override val planFreeSub = "unlimited menu"
    override val planCurrent = "Your plan"
    override val planRecommended = "Recommended"
    override val perMonth = "per month"
    override val proLockTitle = "Available on Pro"
    override val proLockTeamBody = "Invite waiters, cooks and managers on the Pro plan. Current team members stay."
    override val proUpgradeCta = "Upgrade to Pro"
    override val proSectionHint = "Available on the Pro plan — enable it in the Subscription tab."
    override val proLangHint = "Free uses one active menu language — switch it anytime. Pro unlocks all languages."
    override val payHint = "Payment via monobank's secure page. After payment the subscription renews automatically."
    override val paymentHistory = "Payment history"

    override val newCategory = "New category"
    override val addCategory = "Add category"
    override val hiddenBadge = "hidden"
    override val showLabel = "Show"
    override val hideLabel = "Hide"
    override val moreActions = "More actions"
    override val moveUp = "Up"
    override val moveDown = "Down"
    override val deleteCategory = "Delete category"
    override fun deleteCategoryConfirm(name: String) = "Delete category “$name” together with its dishes?"
    override val addDishPlus = "＋ Add dish"
    override val copySuffix = " (copy)"
    override val duplicate = "Duplicate"
    override val dishDuplicated = "Dish duplicated"
    override val duplicateFailed = "Failed to duplicate"
    override val edit = "Edit"
    override val delete = "Delete"
    override fun deleteDishConfirm(name: String) = "Delete “$name”?"
    override val dishDeleted = "Dish deleted"
    override val deleteFailed = "Failed to delete"
    override val dangerZoneTitle = "Danger zone"
    override val dangerZoneHint =
        "Deleting a venue is permanent: the menu, photos, tables, orders, team, loyalty and the site address all disappear."
    override val deleteVenueButton = "Delete venue"
    override val deleteVenueModalTitle = "Delete this venue?"
    override val deleteVenueModalBody =
        "This can't be undone. It permanently removes the menu and photos, tables and QR codes, " +
            "orders and stats, team and invites, the loyalty program and the site address."
    override fun deleteVenueConfirmLabel(name: String) = "Type the name “$name” to confirm"
    override val deleteVenueConfirmPlaceholder = "Venue name"
    override val deleteVenueConfirmCta = "Delete forever"
    override val deleteVenueSuccess = "Venue deleted"
    override val stopBadge = "stop"
    override val descriptionLabel = "Description"
    override val descriptionPh = "E.g.: Tomatoes, mozzarella, fresh basil"
    override val priceLabel = "Price"
    override val weightLabel = "Weight/volume"
    override val weightPh = "320 g"
    override val kcalLabel = "Kcal"
    override val tagsLabel = "Tags"
    override val allergensLabel = "Allergens"
    override val tagVegan = "Vegan"
    override val tagSpicy = "Spicy"
    override val tagNew = "New"
    override val allergenGluten = "Gluten"
    override val allergenLactose = "Lactose"
    override val allergenNuts = "Nuts"
    override val allergenEggs = "Eggs"
    override val allergenFish = "Fish"
    override val allergenSoy = "Soy"
    override val chefRecommends = "Chef recommends"
    override val uploadingEllipsis = "Uploading…"
    override val addPhoto = "Add photo"
    override val replacePhoto = "Replace photo"
    override val photoUploadFailed = "Failed to upload the photo"
    override val addDish = "Add dish"
    override val save = "Save"
    override val dishAdded = "Dish added"
    override val savedToast = "Saved"
    override val saveFailed = "Failed to save"
    override val menuStatCategories = "categories"
    override val menuStatDishes = "dishes"
    override val menuStatFeatured = "featured"
    override val menuEmptyTitle = "Your menu is empty"
    override val menuEmptyHint = "Create your first category — then start adding dishes."
    override val categoryLabel = "Category"
    override val editCategory = "Edit category"
    override val editDish = "Edit dish"
    override val noCategory = "No category"

    override val sitePreviewTitle = "How the site will look"
    override val sitePreviewHint = "The preview updates instantly — save to apply it on the site."
    override val restaurantNamePh = "Restaurant name"
    override val brandTitle = "Brand"
    override val logoLabel = "Logo"
    override val coverLabel = "Cover"
    override val nameLabel = "Name"
    override val shortDescription = "Short description"
    override val accentHint = "Accent colour — buttons, prices and highlights on your site"
    override val customColorLabel = "Or a custom colour"
    override val contactsTitle = "Contacts"
    override val addressLabel = "Address"
    override val mapPickHint = "Drag the map so the pin points at your place"
    override val mapFindByAddress = "Find by address"
    override val mapUseLocation = "My location"
    override val phoneLabel = "Phone"
    override val socialsHint = "Social media — shown to guests in the “About us” section of the site"
    override val workingHoursTitle = "Opening hours"
    override val workingHoursHint = "Empty = day off. Format 09:00 / 22:00, overnight hours are supported too (18:00–02:00)."
    override val dayMon = "Mon"
    override val dayTue = "Tue"
    override val dayWed = "Wed"
    override val dayThu = "Thu"
    override val dayFri = "Fri"
    override val daySat = "Sat"
    override val daySun = "Sun"
    override val dayOff = "day off"
    override val siteThemeTitle = "Site theme"
    override val siteThemeHint = "How the menu looks for guests: light, dark, or automatic based on phone settings."
    override val themeAuto = "Auto (guest's choice)"
    override val themeLight = "Light"
    override val themeDark = "Dark"
    override val menuLookTitle = "Menu appearance"
    override val categoryPhotosTitle = "Photos in categories"
    override val categoryPhotosHint = "A category chip shows the first dish's photo; without a photo — text only"
    override val featureFirstTitle = "First dish as a large card"
    override val featureFirstHint = "The category's first dish gets a full-width photo; when off — same as the rest"
    override val showPopularTitle = "\"Popular\" row"
    override val showPopularHint = "Show a row of popular dishes at the top of the menu"
    override val showChefTitle = "\"Chef recommends\" row"
    override val showChefHint = "Show a row of chef-recommended dishes"
    override val showFiltersTitle = "Filters button"
    override val showFiltersHint = "Show the filters button next to the menu categories"
    override val menuLanguagesTitle = "Menu languages"
    override val menuLanguagesHint = "Guests can switch these languages on the site. Fill in names and descriptions in the chosen languages."
    override val saveChanges = "Save changes"
    override val savedCheck = "Saved ✓"
    override val savedSiteUpdated = "Saved — the site is updated"
    override val saveFailedCheckColor = "Failed to save (check the #RRGGBB colour)"
    override val upload = "Upload"
    override val replace = "Replace"

    override val currencyTitle = "Currency"
    override val currencyHint = "Currency of menu prices and guest bonuses."
    override val guestServicesTitle = "Table services"
    override val guestServicesHint = "What the guest sees on the table page after scanning the QR"
    override val guestCheckTitle = "Show the check to guests"
    override val guestCheckHint = "Guests see their live bill: items, quantity and the total"
    override val waiterCallTitle = "Call a waiter"
    override val waiterCallHint = "Guests can call a waiter or ask for the bill"
    override val loyaltyTitle = "Loyalty program"
    override val loyaltyEnableTitle = "Cashback for guests"
    override val loyaltyEnableHint = "Guests collect bonuses from every bill and redeem them when paying"
    override val loyaltyRateLabel = "Cashback percent of the bill (1–50)"
    override val loyaltyRateHint = "This share of the bill comes back to the guest as bonuses"
    override val loyaltyGuestTitle = "Guest bonuses"
    override val loyaltyCandidatesTitle = "Recently scanned this table"
    override val loyaltyCodePh = "6-digit code"
    override val loyaltyScanQr = "Scan QR"
    override val loyaltyFindGuest = "Find guest"
    override val loyaltyCodeNotFound = "Code not found"
    override val loyaltyCodeExpired = "Code expired — ask the guest to show a new one"
    override val loyaltyCheckFailed = "Could not check the code"
    override val loyaltyBalanceLabel = "Balance"
    override val loyaltyRedeemLabel = "Redeem bonuses"
    override val loyaltyEarnedLabel = "Earned"
    override val loyaltyRedeemedLabel = "Redeemed"
    override val loyaltyNewBalanceLabel = "New balance"
    override val loyaltyClear = "Clear"
    override val loyaltyCameraFailed = "Camera unavailable — type the code manually"
    override val loyaltyGuestAtTable = "Guest at the table"
    override val loyaltyNewGuest = "New guest"
    override val loyaltyVisitsSuffix = "visits"
    override val loyaltyCallingWaiter = "Calling a waiter"
    override val loyaltyCallingBill = "Asking for the bill"
}

object AdminStringsPl : AdminStrings {
    override val tabDashboard = "Przegląd"
    override val tabDashboardSub = "Co się dzieje w Twoim lokalu dzisiaj"
    override val tabMenu = "Menu"
    override val tabMenuSub = "Kategorie, dania, ceny i stop-lista"
    override val tabStopList = "Stop-lista"
    override val tabStopListSub = "Danie się skończyło? Jedno dotknięcie"
    override val tabTables = "Stoliki"
    override val tabTablesSub = "Wezwania z sali i kody QR do druku"
    override val tabStaff = "Zespół"
    override val tabStaffSub = "Zaproszenia i role pracowników"
    override val tabSettings = "Ustawienia"
    override val tabSettingsSub = "Marka, kontakty, godziny otwarcia"
    override val tabSubscription = "Subskrypcja"
    override val tabSubscriptionSub = ""

    override val openSite = "Otwórz stronę"
    override val logout = "Wyloguj się"
    override val viewSite = "zobacz stronę"
    override val roleOwner = "Właściciel"
    override val roleManager = "Menedżer"
    override val roleWaiter = "Kelner"
    override val roleCook = "Kucharz"
    override val roleBartender = "Barman"
    override val roleStaff = "Pracownik"

    override val interfaceLanguage = "Język panelu"
    override val pastDueBanner = "Opłacony okres się skończył. Funkcje Pro podziałają jeszcze kilka dni."
    override val pay = "Zapłać"
    override val statScans = "Skany QR"
    override val periodToday = "Dzisiaj"
    override val periodWeek = "Tydzień"
    override val periodMonth = "Miesiąc"
    override val periodYear = "Rok"
    override val prevPeriod = "Poprzedni okres"
    override val nextPeriod = "Następny okres"
    override val statVisits = "Wizyty"
    override val statRevenue = "Przychód"
    override val statRating = "Ocena"
    override val statDishLikes = "Polubienia dań"
    override val statCookedOrders = "Zamówień przygotowanych"
    override val statCookedDishes = "Dań przygotowanych"
    override val statDrinksMade = "Napojów przygotowanych"
    override val statServedOrders = "Zamówień przyjętych"
    override val statServedDishes = "Pozycji przyjętych"
    override val statTablesServed = "Stolików obsłużonych"
    override val myDishesCooked = "Co przygotowano"
    override val myDrinksMade = "Jakie napoje przygotowano"
    override val myDishesServed = "Co przyjęto"
    override val myTablesTitle = "Stoliki"
    override val myTimelineTitle = "Oś czasu"
    override val myWorkEmpty = "Brak danych za ten okres"
    override val scansByDay = "Dynamika skanowań"
    override fun maxBadge(value: String) = "maks. $value"
    override val peakHours = "Godziny szczytu (skany)"
    override val scansByTable = "Skany wg stolików"
    override val withoutTable = "bez stolika"
    override fun tableN(label: String) = "Stolik $label"
    override val topDishes = "Ulubione dania gości"
    override val soldProducts = "Sprzedane produkty"
    override val dashEmptyTables = "Brak skanów"
    override val dashEmptyTablesHint = "Wydrukuj kody QR stolików — tu pojawią się statystyki dla każdego stolika"
    override val dashEmptySales = "Brak sprzedaży"
    override val dashEmptySalesHint = "Gdy goście zaczną zamawiać, pojawi się tu przychód według dań"
    override val dashEmptyTop = "Brak polubień"
    override val dashEmptyTopHint = "Goście mogą polubić dania w menu — ulubione pojawią się tutaj"
    override val dashEmptyCallsHint = "Brak wezwań w tym okresie"
    override val dashEmptyPeakHint = "Dane pojawią się po pierwszych skanach QR"

    override val tabOrders = "Zamówienia"
    override val tabOrdersSub = "Kuchnia i sala: statusy dań w czasie rzeczywistym"
    override val statusNew = "Nowe"
    override val statusCooking = "W przygotowaniu"
    override val statusReady = "Gotowe"
    override val statusServed = "Podane"
    override val takeOrder = "Zacznij gotować"
    override val markReady = "Gotowe"
    override val markServed = "Podane"
    override val newOrderTitle = "Nowe zamówienie"
    override val addOrderBtn = "Dodaj zamówienie"
    override val addDishesHint = "Dodaj dania z menu"
    override val totalLabel = "Razem"
    override val markPaid = "Opłacone"
    override val confirmPaid = "Oznaczyć jako opłacone i zwolnić stolik?"
    override val tableFree = "Wolny"
    override val noActiveOrders = "Brak aktywnych zamówień"
    override val ordersColumnEmpty = "Pusto"
    override fun orderTakenBy(name: String) = "Przyjął: $name"
    override val orderOtherWaiter = "Zamówienie innego kelnera"
    override val closeMixedTableHint = "Przy stoliku jest zamówienie innego kelnera — zamknie menedżer"
    override val waitingWaiter ="Czeka na kelnera"
    override val closeLabel = "Zamknij"
    override fun minutesShort(min: Long) = "$min min"
    override fun agoShort(min: Long) = when {
        min < 60 -> "$min min"
        min < 1440 -> "${min / 60} godz"
        else -> "${min / 1440} dni"
    }
    override val orderStepBack = "Cofnij"
    override val authCabinet = "Panel Twojego lokalu"
    override val authRegisterSub = "Utwórz konto, aby się zalogować"
    override val authShopName = "Nazwa restauracji"
    override val authShopNamePh = "Kawiarnia «Zacisze»"
    override val authDomain = "Domena"
    override val authDomainPh = "zacisze"
    override val authPassword = "Hasło"
    override val authSignIn = "Zaloguj się"
    override val authCreateAccount = "Utwórz konto"
    override val authCreateShop = "Załóż lokal"
    override val authWizardSub = "Ostatni krok — utwórz swój lokal"
    override val authNoShop = "Nie masz jeszcze lokalu? Zarejestruj"
    override val authHaveAccount = "Już zarejestrowany? Zaloguj się"
    override val authNoAccount = "Nie masz konta? Zarejestruj się"
    override val authOr = "lub"
    override val authBack = "Wstecz"
    override val authContinueGoogle = "Kontynuuj z Google"
    override val authContinueApple = "Kontynuuj z Apple"
    override val authLoginFailed = "Błędny e-mail lub hasło."
    override val authRegisterFailed = "Nie udało się zarejestrować. Sprawdź dane."
    override val authErrEmailInvalid = "Podaj poprawny e-mail — np. you@example.com."
    override val authErrEmailTaken = "Ten e-mail jest już zarejestrowany. Spróbuj się zalogować."
    override val authErrDomainTaken = "Ten adres strony jest zajęty — wybierz inny."
    override val authErrDomainRules =
        "Adres strony: 3–30 znaków, tylko litery łacińskie, cyfry i myślniki; musi zaczynać się i kończyć literą lub cyfrą."
    override val authPasswordHint = "Co najmniej 6 znaków"
    override val authErrPasswordRules =
        "Hasło jest za krótkie: wymagane co najmniej 6 znaków."
    override val authErrInvalidCredentials = "Błędny e-mail lub hasło."
    override val authErrOauthEmailTaken = "Ten e-mail jest już zarejestrowany z hasłem — zaloguj się e-mailem i hasłem."
    override val authErrOauthAccount = "To konto loguje się przez Google/Apple — użyj tego przycisku."
    override val authErrDeactivated = "Konto jest dezaktywowane. Skontaktuj się z pomocą."
    override val authErrDomainReserved = "Ten adres jest zarezerwowany przez system — wybierz inny."
    override val authErrNameRules = "Nazwa lokalu: od 1 do 100 znaków."
    override val brandTagline = "Twoja własna strona w 1 minutę — i od razu do pracy"
    override val brandFeature1 = "Menu QR w kilku językach"
    override val brandFeature2 = "Sala i kuchnia w czasie rzeczywistym"
    override val brandFeature3 = "Statystyki, stop-lista i zespół"
    override val brandFeature4 = "Zamówienia, rachunki i płatność"
    override val brandFeature6 = "Darmowy plan, bez karty"
    override val inviteWelcome = "Dołączanie do zespołu"
    override val inviteAccepting = "Przyjmowanie zaproszenia…"
    override fun inviteJoined(role: String) = "Dołączyłeś jako $role"
    override val inviteGoCabinet = "Przejdź do panelu"
    override val inviteInvalid = "Zaproszenie jest nieprawidłowe lub wygasło"
    override val inviteLoginHint = "Zaloguj się lub zarejestruj, aby przyjąć zaproszenie"
    override val hallCalls = "Wezwania z sali"
    override val legendWaiter = "Kelner"
    override val legendBill = "Rachunek"
    override val asksBill = "prosi o rachunek"
    override val callsWaiter = "wzywa kelnera"
    override val done = "Zrobione"
    override val markedDone = "Oznaczono jako wykonane"
    override val callUpdateFailed = "Nie udało się zaktualizować wezwania"
    override val layoutSaveFailed = "Nie udało się zapisać układu stolików"
    override val bootFailed = "Nie udało się połączyć z serwerem. Sprawdź internet i spróbuj ponownie."
    override val bootRetry = "Spróbuj ponownie"

    override fun inStop(count: Int) = "Na stopie: $count"
    override fun totalDishes(count: Int) = "Wszystkich dań: $count"
    override val searchDishPh = "Szukaj dania…"
    override val nothingFound = "Nic nie znaleziono"
    override val tryAnotherQuery = "Spróbuj innego zapytania"
    override val outOfStock = "niedostępne"
    override fun backInMenu(name: String) = "„$name” znowu w menu"
    override val putToStop = "Dodano do stop-listy"
    override val updateFailed = "Nie udało się zaktualizować"

    override val addTableTitle = "Dodaj stolik"
    override val tableLabelPh = "Np. 5 lub Taras 2"
    override val add = "Dodaj"
    override val addTableFailed = "Nie udało się dodać stolika"
    override fun tablesCount(count: Int) = "Stoliki ($count)"
    override val qrPdf = "QR do PDF"
    override val preparingPdf = "Przygotowujemy PDF…"
    override fun deleteTable(label: String) = "Usuń stolik $label"
    override fun deleteTableConfirm(label: String) = "Usunąć stolik $label?"
    override val planHint = "Przeciągaj stoliki, aby ustawić je jak na sali. Układ zapisuje się w tej przeglądarce."
    override val noTablesTitle = "Nie ma jeszcze stolików"
    override val noTablesHint = "Dodaj stoliki — wygenerujemy PDF z kodami QR do druku"

    override val inviteTitle = "Zaproś pracownika"
    override val inviteHint = "Kelner — stoliki i zamówienia. Kucharz — zamówienia (stoliki tylko podgląd). Menedżer — wszystko poza subskrypcją."
    override val createInvite = "Utwórz zaproszenie"
    override val inviteCreated = "Zaproszenie utworzone"
    override val inviteCreateFailed = "Nie udało się utworzyć zaproszenia"
    override val copy = "Kopiuj"
    override val copied = "Skopiowano"
    override fun teamCount(count: Int) = "Zespół ($count)"
    override val removeFromTeam = "Usuń z zespołu"
    override val removeFromTeamConfirm = "Usunąć z zespołu?"
    override val activeInvites = "Aktywne zaproszenia:"
    override fun validUntil(date: String) = "ważne do $date"
    override val cancel = "Anuluj"
    override val teamMembersWord = "członków"
    override fun teamMemberSince(date: String) = "w zespole od $date"
    override val permWaiter = "Stoliki i zamówienia"
    override val permCook = "Zamówienia do kuchni"
    override val permBartender = "Zamówienia do baru"
    override val permManager = "Wszystko poza subskrypcją"
    override val barCategory = "Kategoria barowa (napoje)"
    override val barBadge = "Bar"

    override val currentState = "Obecny stan"
    override fun activeUntil(date: String) = "Aktywna do $date"
    override val pastDueLong = "Płatność zaległa — funkcje Pro wkrótce zostaną wstrzymane"
    override val badgeActive = "Aktywna"
    override val badgePastDue = "Zaległa"
    override val plan1Month = "1 miesiąc"
    override val plan3Months = "3 miesiące"
    override val plan12Months = "12 miesięcy"
    override val bestValueBadge = "-17% · korzystnie"
    override val payMono = "Wybierz"
    override val autoRenewLabel = "Automatyczne odnawianie"
    override val autoRenewHint = "Karta zostanie zapisana po płatności, a subskrypcja odnowi się automatycznie. Możesz to wyłączyć w każdej chwili."
    override val autoRenewActive = { mask: String? ->
        "Automatyczne odnawianie włączone" + (mask?.let { " · karta •••• $it" } ?: "")
    }
    override val autoRenewDisable = "Wyłącz"
    override val planActiveTitle = "Plan aktywny"
    override val nextChargeOn = { date: String, amount: String -> "Następna płatność $date · $amount" }
    override val autoRenewOffNote = { date: String -> "Automatyczne odnawianie wyłączone — dostęp do $date" }
    override val cancelSubscription = "Anuluj subskrypcję"
    override val resumeAutoRenew = "Wznów automatyczne odnawianie"
    override val subActiveOverline = "Subskrypcja aktywna"
    override val subEyebrow = "Plany"
    override val subChooseTitle = "Free albo Pro"
    override val subChooseSub = "Menu, zamówienia i stoliki — za darmo na zawsze. Pro odblokowuje usługi dla gości i zespół."
    override val subFeaturesCap = "W planie Pro"
    override val subFeatures = listOf(
        "Program lojalnościowy z cashbackiem",
        "Przycisk wezwania kelnera",
        "Rachunek stolika dla gości",
        "Menu w wielu językach",
        "Sekcje „Popularne” i „Szef kuchni poleca”",
        "Zespół i zaproszenia personelu",
    )
    override val subCtaSubscribe = "Przejdź na Pro"
    override val trustCancel = "Anuluj w każdej chwili"
    override val trustSecure = "Bezpieczna płatność"

    override val planFree = "Free"
    override val planPro = "Pro"
    override val planFreeSub = "menu bez ograniczeń"
    override val planCurrent = "Twój plan"
    override val planRecommended = "Polecamy"
    override val perMonth = "miesięcznie"
    override val proLockTitle = "Dostępne w Pro"
    override val proLockTeamBody = "Zapraszaj kelnerów, kucharzy i menedżerów w planie Pro. Obecni członkowie zespołu zostają."
    override val proUpgradeCta = "Przejdź na Pro"
    override val proSectionHint = "Dostępne w planie Pro — włącz go w zakładce „Subskrypcja”."
    override val proLangHint = "W planie Free aktywny jest jeden język menu — możesz go zmienić w każdej chwili. Pro odblokowuje wszystkie języki."
    override val payHint = "Płatność przez bezpieczną stronę monobank. Po opłaceniu subskrypcja przedłuży się automatycznie."
    override val paymentHistory = "Historia płatności"

    override val newCategory = "Nowa kategoria"
    override val addCategory = "Dodaj kategorię"
    override val hiddenBadge = "ukryta"
    override val showLabel = "Pokaż"
    override val hideLabel = "Ukryj"
    override val moreActions = "Więcej akcji"
    override val moveUp = "W górę"
    override val moveDown = "W dół"
    override val deleteCategory = "Usuń kategorię"
    override fun deleteCategoryConfirm(name: String) = "Usunąć kategorię „$name” razem z daniami?"
    override val addDishPlus = "＋ Dodaj danie"
    override val copySuffix = " (kopia)"
    override val duplicate = "Duplikuj"
    override val dishDuplicated = "Danie zduplikowane"
    override val duplicateFailed = "Nie udało się zduplikować"
    override val edit = "Edytuj"
    override val delete = "Usuń"
    override fun deleteDishConfirm(name: String) = "Usunąć „$name”?"
    override val dishDeleted = "Danie usunięte"
    override val deleteFailed = "Nie udało się usunąć"
    override val dangerZoneTitle = "Strefa zagrożenia"
    override val dangerZoneHint =
        "Usunięcie lokalu jest nieodwracalne: znikną menu, zdjęcia, stoliki, zamówienia, zespół, program lojalnościowy i adres strony."
    override val deleteVenueButton = "Usuń lokal"
    override val deleteVenueModalTitle = "Usunąć ten lokal?"
    override val deleteVenueModalBody =
        "Tej operacji nie można cofnąć. Na zawsze usuwa menu i zdjęcia, stoliki i kody QR, " +
            "zamówienia i statystyki, zespół i zaproszenia, program lojalnościowy oraz adres strony."
    override fun deleteVenueConfirmLabel(name: String) = "Aby potwierdzić, wpisz nazwę „$name”"
    override val deleteVenueConfirmPlaceholder = "Nazwa lokalu"
    override val deleteVenueConfirmCta = "Usuń na zawsze"
    override val deleteVenueSuccess = "Lokal usunięty"
    override val stopBadge = "stop"
    override val descriptionLabel = "Opis"
    override val descriptionPh = "Np.: Pomidory, mozzarella, świeża bazylia"
    override val priceLabel = "Cena"
    override val weightLabel = "Waga/objętość"
    override val weightPh = "320 g"
    override val kcalLabel = "Kcal"
    override val tagsLabel = "Tagi"
    override val allergensLabel = "Alergeny"
    override val tagVegan = "Wegańskie"
    override val tagSpicy = "Ostre"
    override val tagNew = "Nowość"
    override val allergenGluten = "Gluten"
    override val allergenLactose = "Laktoza"
    override val allergenNuts = "Orzechy"
    override val allergenEggs = "Jajka"
    override val allergenFish = "Ryba"
    override val allergenSoy = "Soja"
    override val chefRecommends = "Szef kuchni poleca"
    override val uploadingEllipsis = "Przesyłanie…"
    override val addPhoto = "Dodaj zdjęcie"
    override val replacePhoto = "Zamień zdjęcie"
    override val photoUploadFailed = "Nie udało się przesłać zdjęcia"
    override val addDish = "Dodaj danie"
    override val save = "Zapisz"
    override val dishAdded = "Danie dodane"
    override val savedToast = "Zapisano"
    override val saveFailed = "Nie udało się zapisać"
    override val menuStatCategories = "kategorii"
    override val menuStatDishes = "dań"
    override val menuStatFeatured = "polecanych"
    override val menuEmptyTitle = "Menu jest puste"
    override val menuEmptyHint = "Utwórz pierwszą kategorię — a potem dodawaj dania."
    override val categoryLabel = "Kategoria"
    override val editCategory = "Edytuj kategorię"
    override val editDish = "Edytuj danie"
    override val noCategory = "Bez kategorii"

    override val sitePreviewTitle = "Jak będzie wyglądać strona"
    override val sitePreviewHint = "Podgląd aktualizuje się od razu — zapisz, aby zastosować na stronie."
    override val restaurantNamePh = "Nazwa lokalu"
    override val brandTitle = "Marka"
    override val logoLabel = "Logo"
    override val coverLabel = "Okładka"
    override val nameLabel = "Nazwa"
    override val shortDescription = "Krótki opis"
    override val accentHint = "Kolor akcentu — przyciski, ceny i wyróżnienia na stronie lokalu"
    override val customColorLabel = "Lub własny kolor"
    override val contactsTitle = "Kontakty"
    override val addressLabel = "Adres"
    override val mapPickHint = "Przesuń mapę tak, aby znacznik wskazywał lokal"
    override val mapFindByAddress = "Znajdź po adresie"
    override val mapUseLocation = "Moja lokalizacja"
    override val phoneLabel = "Telefon"
    override val socialsHint = "Media społecznościowe — widoczne dla gości w sekcji „O nas” na stronie"
    override val workingHoursTitle = "Godziny otwarcia"
    override val workingHoursHint = "Puste = dzień wolny. Format 09:00 / 22:00, obsługiwane też przez północ (18:00–02:00)."
    override val dayMon = "Pon"
    override val dayTue = "Wt"
    override val dayWed = "Śr"
    override val dayThu = "Czw"
    override val dayFri = "Pt"
    override val daySat = "Sob"
    override val daySun = "Nd"
    override val dayOff = "dzień wolny"
    override val siteThemeTitle = "Motyw strony"
    override val siteThemeHint = "Jak menu wygląda u gościa: jasny, ciemny lub automatycznie wg ustawień telefonu."
    override val themeAuto = "Auto (jak u gościa)"
    override val themeLight = "Jasny"
    override val themeDark = "Ciemny"
    override val menuLookTitle = "Wygląd menu"
    override val categoryPhotosTitle = "Zdjęcia w kategoriach"
    override val categoryPhotosHint = "Chip kategorii pokazuje zdjęcie pierwszego dania; bez zdjęcia — tylko tekst"
    override val featureFirstTitle = "Pierwsze danie jako duża karta"
    override val featureFirstHint = "Pierwsze danie kategorii — duże zdjęcie na całą szerokość; wyłączone — jak pozostałe"
    override val showPopularTitle = "Sekcja „Popularne”"
    override val showPopularHint = "Pokaż zestaw popularnych dań na górze menu"
    override val showChefTitle = "Sekcja „Szef poleca”"
    override val showChefHint = "Pokaż zestaw dań polecanych przez szefa kuchni"
    override val showFiltersTitle = "Przycisk filtrów"
    override val showFiltersHint = "Pokaż przycisk filtrów obok kategorii menu"
    override val menuLanguagesTitle = "Języki menu"
    override val menuLanguagesHint = "Gość może przełączać te języki na stronie. Uzupełniaj nazwy i opisy w wybranych językach."
    override val saveChanges = "Zapisz zmiany"
    override val savedCheck = "Zapisano ✓"
    override val savedSiteUpdated = "Zapisano — strona zaktualizowana"
    override val saveFailedCheckColor = "Nie udało się zapisać (sprawdź kolor #RRGGBB)"
    override val upload = "Prześlij"
    override val replace = "Zamień"

    override val currencyTitle = "Waluta"
    override val currencyHint = "Waluta cen w menu i bonusów gości."
    override val guestServicesTitle = "Usługi przy stoliku"
    override val guestServicesHint = "Co gość widzi na stronie stolika po zeskanowaniu QR"
    override val guestCheckTitle = "Pokazuj rachunek gościom"
    override val guestCheckHint = "Gość widzi swój rachunek na żywo: pozycje, ilość i sumę"
    override val waiterCallTitle = "Wezwanie kelnera"
    override val waiterCallHint = "Gość może wezwać kelnera lub poprosić o rachunek"
    override val loyaltyTitle = "Program lojalnościowy"
    override val loyaltyEnableTitle = "Cashback dla gości"
    override val loyaltyEnableHint = "Gość zbiera bonusy z każdego rachunku i wykorzystuje je przy płatności"
    override val loyaltyRateLabel = "Procent cashbacku z rachunku (1–50)"
    override val loyaltyRateHint = "Tyle procent kwoty rachunku wraca do gościa w bonusach"
    override val loyaltyGuestTitle = "Bonusy gościa"
    override val loyaltyCandidatesTitle = "Niedawno skanowali ten stolik"
    override val loyaltyCodePh = "6-cyfrowy kod"
    override val loyaltyScanQr = "Skanuj QR"
    override val loyaltyFindGuest = "Znajdź gościa"
    override val loyaltyCodeNotFound = "Nie znaleziono kodu"
    override val loyaltyCodeExpired = "Kod wygasł — poproś gościa o nowy"
    override val loyaltyCheckFailed = "Nie udało się sprawdzić kodu"
    override val loyaltyBalanceLabel = "Saldo"
    override val loyaltyRedeemLabel = "Wykorzystaj bonusy"
    override val loyaltyEarnedLabel = "Naliczono"
    override val loyaltyRedeemedLabel = "Wykorzystano"
    override val loyaltyNewBalanceLabel = "Nowe saldo"
    override val loyaltyClear = "Wyczyść"
    override val loyaltyCameraFailed = "Kamera niedostępna — wpisz kod ręcznie"
    override val loyaltyGuestAtTable = "Gość przy stoliku"
    override val loyaltyNewGuest = "Nowy gość"
    override val loyaltyVisitsSuffix = "wizyt"
    override val loyaltyCallingWaiter = "Woła kelnera"
    override val loyaltyCallingBill = "Prosi o rachunek"
}

object AdminStringsRu : AdminStrings {
    override val tabDashboard = "Обзор"
    override val tabDashboardSub = "Что происходит с заведением сегодня"
    override val tabMenu = "Меню"
    override val tabMenuSub = "Категории, блюда, цены и стоп-лист"
    override val tabStopList = "Стоп-лист"
    override val tabStopListSub = "Блюдо закончилось? Одно касание"
    override val tabTables = "Столы"
    override val tabTablesSub = "Вызовы из зала и QR-коды для печати"
    override val tabStaff = "Команда"
    override val tabStaffSub = "Приглашения и роли сотрудников"
    override val tabSettings = "Настройки"
    override val tabSettingsSub = "Бренд, контакты, часы работы"
    override val tabSubscription = "Подписка"
    override val tabSubscriptionSub = ""

    override val openSite = "Открыть сайт"
    override val logout = "Выйти"
    override val viewSite = "посмотреть сайт"
    override val roleOwner = "Владелец"
    override val roleManager = "Менеджер"
    override val roleWaiter = "Официант"
    override val roleCook = "Повар"
    override val roleBartender = "Бармен"
    override val roleStaff = "Сотрудник"

    override val interfaceLanguage = "Язык панели"
    override val pastDueBanner = "Оплаченный период закончился. Pro-функции работают ещё несколько дней."
    override val pay = "Оплатить"
    override val statScans = "Сканирования QR"
    override val periodToday = "Сегодня"
    override val periodWeek = "Неделя"
    override val periodMonth = "Месяц"
    override val periodYear = "Год"
    override val prevPeriod = "Предыдущий период"
    override val nextPeriod = "Следующий период"
    override val statVisits = "Визитов"
    override val statRevenue = "Выручка"
    override val statRating = "Рейтинг"
    override val statDishLikes = "Лайков блюд"
    override val statCookedOrders = "Заказов приготовлено"
    override val statCookedDishes = "Блюд приготовлено"
    override val statDrinksMade = "Напитков сделано"
    override val statServedOrders = "Заказов принято"
    override val statServedDishes = "Товаров принято"
    override val statTablesServed = "Столов обслужено"
    override val myDishesCooked = "Что приготовлено"
    override val myDrinksMade = "Какие напитки сделаны"
    override val myDishesServed = "Что принято"
    override val myTablesTitle = "Столы"
    override val myTimelineTitle = "Хронология"
    override val myWorkEmpty = "За этот период пока нет данных"
    override val scansByDay = "Динамика сканирований"
    override fun maxBadge(value: String) = "макс. $value"
    override val peakHours = "Пик часов (сканы)"
    override val scansByTable = "Сканы по столам"
    override val withoutTable = "без стола"
    override fun tableN(label: String) = "Стол $label"
    override val topDishes = "Любимые блюда гостей"
    override val soldProducts = "Проданные товары"
    override val dashEmptyTables = "Сканирований пока нет"
    override val dashEmptyTablesHint = "Распечатайте QR-коды столов — здесь появится статистика по каждому столу"
    override val dashEmptySales = "Продаж пока нет"
    override val dashEmptySalesHint = "Как только гости начнут заказывать, здесь появится выручка по блюдам"
    override val dashEmptyTop = "Лайков пока нет"
    override val dashEmptyTopHint = "Гости могут лайкать блюда в меню — любимые появятся здесь"
    override val dashEmptyCallsHint = "Вызовов за этот период не было"
    override val dashEmptyPeakHint = "Данные появятся после первых сканирований QR"

    override val tabOrders = "Заказы"
    override val tabOrdersSub = "Кухня и зал: статусы блюд в реальном времени"
    override val statusNew = "Новый"
    override val statusCooking = "Готовится"
    override val statusReady = "Готов"
    override val statusServed = "Подан"
    override val takeOrder = "Взять в работу"
    override val markReady = "Готово"
    override val markServed = "Подано"
    override val newOrderTitle = "Новый заказ"
    override val addOrderBtn = "Добавить заказ"
    override val addDishesHint = "Добавьте блюда из меню"
    override val totalLabel = "Итого"
    override val markPaid = "Оплачено"
    override val confirmPaid = "Отметить оплаченным и освободить стол?"
    override val tableFree = "Свободен"
    override val noActiveOrders = "Нет активных заказов"
    override val ordersColumnEmpty = "Пусто"
    override fun orderTakenBy(name: String) = "Принял: $name"
    override val orderOtherWaiter = "Заказ другого официанта"
    override val closeMixedTableHint = "За столом есть заказ другого официанта — закрыть может менеджер"
    override val waitingWaiter ="Ждёт официанта"
    override val closeLabel = "Закрыть"
    override fun minutesShort(min: Long) = "$min мин"
    override fun agoShort(min: Long) = when {
        min < 60 -> "$min мин"
        min < 1440 -> "${min / 60} ч"
        else -> "${min / 1440} дн"
    }
    override val orderStepBack = "Вернуть назад"
    override val authCabinet = "Кабинет вашего заведения"
    override val authRegisterSub = "Создайте аккаунт для входа"
    override val authShopName = "Название ресторана"
    override val authShopNamePh = "Кофейня «Уют»"
    override val authDomain = "Домен"
    override val authDomainPh = "uyut"
    override val authPassword = "Пароль"
    override val authSignIn = "Войти"
    override val authCreateAccount = "Создать аккаунт"
    override val authCreateShop = "Создать заведение"
    override val authWizardSub = "Последний шаг — создайте своё заведение"
    override val authNoShop = "Ещё нет заведения? Зарегистрировать"
    override val authHaveAccount = "Уже зарегистрированы? Войти"
    override val authNoAccount = "Нет аккаунта? Зарегистрироваться"
    override val authOr = "или"
    override val authBack = "Назад"
    override val authContinueGoogle = "Продолжить с Google"
    override val authContinueApple = "Продолжить с Apple"
    override val authLoginFailed = "Неверный email или пароль."
    override val authRegisterFailed = "Не удалось зарегистрироваться. Проверьте данные."
    override val authErrEmailInvalid = "Укажите корректный email — например, you@example.com."
    override val authErrEmailTaken = "Этот email уже зарегистрирован. Попробуйте войти."
    override val authErrDomainTaken = "Этот адрес сайта уже занят — выберите другой."
    override val authErrDomainRules =
        "Адрес сайта: 3–30 символов, только латинские буквы, цифры и дефис; начинается и заканчивается буквой или цифрой."
    override val authPasswordHint = "Минимум 6 символов"
    override val authErrPasswordRules =
        "Пароль слишком короткий: нужно минимум 6 символов."
    override val authErrInvalidCredentials = "Неверный email или пароль."
    override val authErrOauthAccount = "Этот аккаунт входит через Google/Apple — воспользуйтесь этой кнопкой входа."
    override val authErrOauthEmailTaken = "Этот email уже зарегистрирован с паролем — войдите по email и паролю."
    override val authErrDeactivated = "Аккаунт деактивирован. Напишите в поддержку."
    override val authErrDomainReserved = "Этот адрес зарезервирован системой — выберите другой."
    override val authErrNameRules = "Название заведения: от 1 до 100 символов."
    override val brandTagline = "Свой сайт за 1 минуту — и сразу к работе"
    override val brandFeature1 = "QR-меню на нескольких языках"
    override val brandFeature2 = "Зал и кухня в реальном времени"
    override val brandFeature3 = "Статистика, стоп-лист и команда"
    override val brandFeature4 = "Заказы, счета и оплата"
    override val brandFeature6 = "Бесплатный план, без карты"
    override val inviteWelcome = "Присоединение к команде"
    override val inviteAccepting = "Принимаем приглашение…"
    override fun inviteJoined(role: String) = "Вы присоединились как $role"
    override val inviteGoCabinet = "Перейти в кабинет"
    override val inviteInvalid = "Приглашение недействительно или просрочено"
    override val inviteLoginHint = "Войдите или зарегистрируйтесь, чтобы принять приглашение"
    override val hallCalls = "Вызовы из зала"
    override val legendWaiter = "Официант"
    override val legendBill = "Счёт"
    override val asksBill = "просит счёт"
    override val callsWaiter = "зовёт официанта"
    override val done = "Выполнено"
    override val markedDone = "Отмечено выполненным"
    override val callUpdateFailed = "Не удалось обновить вызов"
    override val layoutSaveFailed = "Не удалось сохранить расположение столов"
    override val bootFailed = "Не удалось связаться с сервером. Проверьте интернет и попробуйте ещё раз."
    override val bootRetry = "Повторить"

    override fun inStop(count: Int) = "В стопе: $count"
    override fun totalDishes(count: Int) = "Всего блюд: $count"
    override val searchDishPh = "Поиск блюда…"
    override val nothingFound = "Ничего не найдено"
    override val tryAnotherQuery = "Попробуйте другой запрос"
    override val outOfStock = "нет в наличии"
    override fun backInMenu(name: String) = "«$name» снова в меню"
    override val putToStop = "Поставлено в стоп"
    override val updateFailed = "Не удалось обновить"

    override val addTableTitle = "Добавить стол"
    override val tableLabelPh = "Напр. 5 или Терраса 2"
    override val add = "Добавить"
    override val addTableFailed = "Не удалось добавить стол"
    override fun tablesCount(count: Int) = "Столы ($count)"
    override val qrPdf = "QR в PDF"
    override val preparingPdf = "Готовим PDF…"
    override fun deleteTable(label: String) = "Удалить стол $label"
    override fun deleteTableConfirm(label: String) = "Удалить стол $label?"
    override val planHint = "Перетаскивайте столы, чтобы расставить их как в зале. Расстановка сохраняется в этом браузере."
    override val noTablesTitle = "Пока нет столов"
    override val noTablesHint = "Добавьте столы — и мы сгенерируем PDF с QR-кодами для печати"

    override val inviteTitle = "Пригласить сотрудника"
    override val inviteHint = "Официант — столы и заказы. Повар — заказы (столы только просмотр). Менеджер — всё, кроме подписки."
    override val createInvite = "Создать приглашение"
    override val inviteCreated = "Приглашение создано"
    override val inviteCreateFailed = "Не удалось создать приглашение"
    override val copy = "Копировать"
    override val copied = "Скопировано"
    override fun teamCount(count: Int) = "Команда ($count)"
    override val removeFromTeam = "Убрать из команды"
    override val removeFromTeamConfirm = "Убрать из команды?"
    override val activeInvites = "Активные приглашения:"
    override fun validUntil(date: String) = "действует до $date"
    override val cancel = "Отменить"
    override val teamMembersWord = "участников"
    override fun teamMemberSince(date: String) = "в команде с $date"
    override val permWaiter = "Столы и заказы"
    override val permCook = "Заказы на кухню"
    override val permBartender = "Заказы на бар"
    override val permManager = "Всё, кроме подписки"
    override val barCategory = "Барная категория (напитки)"
    override val barBadge = "Бар"

    override val currentState = "Текущее состояние"
    override fun activeUntil(date: String) = "Активна до $date"
    override val pastDueLong = "Оплата просрочена — Pro-функции скоро отключатся"
    override val badgeActive = "Активна"
    override val badgePastDue = "Просрочено"
    override val plan1Month = "1 месяц"
    override val plan3Months = "3 месяца"
    override val plan12Months = "12 месяцев"
    override val bestValueBadge = "-17% · выгодно"
    override val payMono = "Выбрать"
    override val autoRenewLabel = "Автопродление"
    override val autoRenewHint = "Карта сохранится после оплаты, и подписка будет продлеваться автоматически. Отключить можно в любой момент."
    override val autoRenewActive = { mask: String? ->
        "Автопродление включено" + (mask?.let { " · карта •••• $it" } ?: "")
    }
    override val autoRenewDisable = "Отключить"
    override val planActiveTitle = "План активирован"
    override val nextChargeOn = { date: String, amount: String -> "Следующее списание $date · $amount" }
    override val autoRenewOffNote = { date: String -> "Автопродление отключено — доступ действует до $date" }
    override val cancelSubscription = "Отменить подписку"
    override val resumeAutoRenew = "Возобновить автопродление"
    override val subActiveOverline = "Подписка активна"
    override val subEyebrow = "Тарифы"
    override val subChooseTitle = "Free или Pro"
    override val subChooseSub = "Меню, заказы и столы — бесплатно навсегда. Pro открывает сервисы для гостей и команду."
    override val subFeaturesCap = "Входит в Pro"
    override val subFeatures = listOf(
        "Программа лояльности с кешбэком",
        "Кнопка вызова официанта",
        "Счёт стола для гостей",
        "Меню на нескольких языках",
        "Разделы «Популярное» и «Шеф рекомендует»",
        "Команда и приглашения персонала",
    )
    override val subCtaSubscribe = "Перейти на Pro"
    override val trustCancel = "Отменить в любой момент"
    override val trustSecure = "Защищённая оплата"

    override val planFree = "Free"
    override val planPro = "Pro"
    override val planFreeSub = "меню без ограничений"
    override val planCurrent = "Ваш план"
    override val planRecommended = "Рекомендуем"
    override val perMonth = "в месяц"
    override val proLockTitle = "Доступно на Pro"
    override val proLockTeamBody = "Приглашайте официантов, поваров и менеджеров на тарифе Pro. Текущие участники команды остаются."
    override val proUpgradeCta = "Перейти на Pro"
    override val proSectionHint = "Доступно на тарифе Pro — включите его во вкладке «Подписка»."
    override val proLangHint = "На Free активен один язык меню — сменить его можно в любой момент. Все языки открывает Pro."
    override val payHint = "Оплата через защищённую страницу monobank. После оплаты подписка продлится автоматически."
    override val paymentHistory = "История платежей"

    override val newCategory = "Новая категория"
    override val addCategory = "Добавить категорию"
    override val hiddenBadge = "скрыто"
    override val showLabel = "Показать"
    override val hideLabel = "Скрыть"
    override val moreActions = "Больше действий"
    override val moveUp = "Вверх"
    override val moveDown = "Вниз"
    override val deleteCategory = "Удалить категорию"
    override fun deleteCategoryConfirm(name: String) = "Удалить категорию «$name» вместе с блюдами?"
    override val addDishPlus = "＋ Добавить блюдо"
    override val copySuffix = " (копия)"
    override val duplicate = "Дублировать"
    override val dishDuplicated = "Блюдо продублировано"
    override val duplicateFailed = "Не удалось дублировать"
    override val edit = "Редактировать"
    override val delete = "Удалить"
    override fun deleteDishConfirm(name: String) = "Удалить «$name»?"
    override val dishDeleted = "Блюдо удалено"
    override val deleteFailed = "Не удалось удалить"
    override val dangerZoneTitle = "Опасная зона"
    override val dangerZoneHint =
        "Удаление заведения необратимо: исчезнут меню, фото, столы, заказы, команда, лояльность и адрес сайта."
    override val deleteVenueButton = "Удалить заведение"
    override val deleteVenueModalTitle = "Удалить заведение?"
    override val deleteVenueModalBody =
        "Действие необратимо. Навсегда удаляются меню и фото, столы и QR-коды, " +
            "заказы и статистика, команда и приглашения, программа лояльности и адрес сайта."
    override fun deleteVenueConfirmLabel(name: String) = "Чтобы подтвердить, введите название «$name»"
    override val deleteVenueConfirmPlaceholder = "Название заведения"
    override val deleteVenueConfirmCta = "Удалить навсегда"
    override val deleteVenueSuccess = "Заведение удалено"
    override val stopBadge = "стоп"
    override val descriptionLabel = "Описание"
    override val descriptionPh = "Напр.: Томаты, моцарелла, свежий базилик"
    override val priceLabel = "Цена"
    override val weightLabel = "Вес/объём"
    override val weightPh = "320 г"
    override val kcalLabel = "Ккал"
    override val tagsLabel = "Теги"
    override val allergensLabel = "Аллергены"
    override val tagVegan = "Веган"
    override val tagSpicy = "Острое"
    override val tagNew = "Новинка"
    override val allergenGluten = "Глютен"
    override val allergenLactose = "Лактоза"
    override val allergenNuts = "Орехи"
    override val allergenEggs = "Яйца"
    override val allergenFish = "Рыба"
    override val allergenSoy = "Соя"
    override val chefRecommends = "Рекомендует шеф"
    override val uploadingEllipsis = "Загрузка…"
    override val addPhoto = "Добавить фото"
    override val replacePhoto = "Заменить фото"
    override val photoUploadFailed = "Не удалось загрузить фото"
    override val addDish = "Добавить блюдо"
    override val save = "Сохранить"
    override val dishAdded = "Блюдо добавлено"
    override val savedToast = "Сохранено"
    override val saveFailed = "Не удалось сохранить"
    override val menuStatCategories = "категорий"
    override val menuStatDishes = "блюд"
    override val menuStatFeatured = "хитов"
    override val menuEmptyTitle = "Меню пока пустое"
    override val menuEmptyHint = "Создайте первую категорию — и начните добавлять блюда."
    override val categoryLabel = "Категория"
    override val editCategory = "Редактировать категорию"
    override val editDish = "Редактировать блюдо"
    override val noCategory = "Без категории"

    override val sitePreviewTitle = "Как будет выглядеть сайт"
    override val sitePreviewHint = "Превью обновляется сразу — сохраните, чтобы применить на сайте."
    override val restaurantNamePh = "Название заведения"
    override val brandTitle = "Бренд"
    override val logoLabel = "Логотип"
    override val coverLabel = "Обложка"
    override val nameLabel = "Название"
    override val shortDescription = "Короткое описание"
    override val accentHint = "Акцентный цвет — кнопки, цены и подсветки на сайте заведения"
    override val customColorLabel = "Или свой цвет"
    override val contactsTitle = "Контакты"
    override val addressLabel = "Адрес"
    override val mapPickHint = "Подвиньте карту так, чтобы маркер указывал на заведение"
    override val mapFindByAddress = "Найти по адресу"
    override val mapUseLocation = "Моё местоположение"
    override val phoneLabel = "Телефон"
    override val socialsHint = "Соцсети — показываются гостям в разделе «Про нас» на сайте"
    override val workingHoursTitle = "Часы работы"
    override val workingHoursHint = "Пусто = выходной. Формат 09:00 / 22:00, поддерживается и через полночь (18:00–02:00)."
    override val dayMon = "Пн"
    override val dayTue = "Вт"
    override val dayWed = "Ср"
    override val dayThu = "Чт"
    override val dayFri = "Пт"
    override val daySat = "Сб"
    override val daySun = "Вс"
    override val dayOff = "выходной"
    override val siteThemeTitle = "Тема сайта"
    override val siteThemeHint = "Как меню выглядит у гостя: светлая, тёмная или автоматически по настройкам телефона."
    override val themeAuto = "Авто (как у гостя)"
    override val themeLight = "Светлая"
    override val themeDark = "Тёмная"
    override val menuLookTitle = "Вид меню"
    override val categoryPhotosTitle = "Фото в категориях"
    override val categoryPhotosHint = "Чип категории показывает фото первого блюда; без фото — только текст"
    override val featureFirstTitle = "Первое блюдо большой карточкой"
    override val featureFirstHint = "Первое блюдо категории — большое фото на всю ширину; выключено — как остальные"
    override val showPopularTitle = "Блок «Популярное»"
    override val showPopularHint = "Показывать подборку популярных блюд вверху меню"
    override val showChefTitle = "Блок «Рекомендует шеф»"
    override val showChefHint = "Показывать подборку блюд, рекомендованных шефом"
    override val showFiltersTitle = "Кнопка фильтров"
    override val showFiltersHint = "Показывать кнопку фильтров напротив категорий меню"
    override val menuLanguagesTitle = "Языки меню"
    override val menuLanguagesHint = "Гость сможет переключать эти языки на сайте. Заполняйте названия и описания на выбранных языках."
    override val saveChanges = "Сохранить изменения"
    override val savedCheck = "Сохранено ✓"
    override val savedSiteUpdated = "Сохранено — сайт обновлён"
    override val saveFailedCheckColor = "Не удалось сохранить (проверьте цвет #RRGGBB)"
    override val upload = "Загрузить"
    override val replace = "Заменить"

    override val currencyTitle = "Валюта"
    override val currencyHint = "Валюта цен в меню и бонусов гостей."
    override val guestServicesTitle = "Сервисы за столом"
    override val guestServicesHint = "Что гость видит на странице стола после сканирования QR"
    override val guestCheckTitle = "Показывать чек гостю"
    override val guestCheckHint = "Гость видит свой счёт в реальном времени: позиции, количество и сумму"
    override val waiterCallTitle = "Вызов официанта"
    override val waiterCallHint = "Гость может позвать официанта или попросить счёт"
    override val loyaltyTitle = "Программа лояльности"
    override val loyaltyEnableTitle = "Кэшбек для гостей"
    override val loyaltyEnableHint = "Гость копит бонусы с каждого чека и списывает их при оплате"
    override val loyaltyRateLabel = "Процент кэшбека с чека (1–50)"
    override val loyaltyRateHint = "Столько процентов от суммы чека вернётся гостю бонусами"
    override val loyaltyGuestTitle = "Бонусы гостя"
    override val loyaltyCandidatesTitle = "Недавно сканировали этот стол"
    override val loyaltyCodePh = "6-значный код"
    override val loyaltyScanQr = "Сканировать QR"
    override val loyaltyFindGuest = "Найти гостя"
    override val loyaltyCodeNotFound = "Код не найден"
    override val loyaltyCodeExpired = "Код просрочен — попросите гостя показать новый"
    override val loyaltyCheckFailed = "Не удалось проверить код"
    override val loyaltyBalanceLabel = "Баланс"
    override val loyaltyRedeemLabel = "Списать бонусы"
    override val loyaltyEarnedLabel = "Начислено"
    override val loyaltyRedeemedLabel = "Списано"
    override val loyaltyNewBalanceLabel = "Новый баланс"
    override val loyaltyClear = "Очистить"
    override val loyaltyCameraFailed = "Камера недоступна — введите код вручную"
    override val loyaltyGuestAtTable = "Гость за столом"
    override val loyaltyNewGuest = "Новый гость"
    override val loyaltyVisitsSuffix = "визитов"
    override val loyaltyCallingWaiter = "Зовёт официанта"
    override val loyaltyCallingBill = "Просит счёт"
}
